<template>
  <section :class="`${tableId}-container`">
    <quick-alert-modal v-bind="quick_alert" />
    <vjs-modal :name="item_create_modal"
               :title="`Create ${getItemLabel(true)}`"
               icon="la la-plus-circle">
      <item-creation-form />
    </vjs-modal>

    <f-data-table :id="tableId"
                  :data-uri="data_uri"
                  :static-params="static_params"
                  :columns="columns"
                  :pagination="{
                    enabled: true,
                    per_page: 25,
                    default_page: 1,
                  }"
                  :sorting="{
                    enabled: true,
                    sort_whole_set: true,
                  }"
                  :server-side="true"
                  :heading="getItemLabel(true) + 's'"
                  :auto-complete-columns="false"
                  :auto-hide-columns="true"
                  :selectable-rows="true"
                  :expanded="true"
                  :locked="true"
                  icon="la la-file-audio-o">
      <!-- ACTIONS -->
      <template #_actions="props">
        <button type="button" class="btn btn-ss-standout" @click="showItemCreationModal()">
          <i class="flaticon2-plus" />New {{ getItemLabel() }}
        </button>
        <button type="button"
                class="btn btn-ss btn-icon-sm"
                @click="props.reloadData">
          <i class="flaticon2-refresh" />Reload Data
        </button>
      </template>
      <template #_row_controls="props">
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: archiveItems})">
          <loader v-if="loading.archive" small class="mr-2" />
          Archive
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: unarchiveItems})">
          <loader v-if="loading.unarchive" small class="mr-2" />
          Un-archive
        </button>
        <button v-if="customer.aiir_client_id !== null"
                v-perms="['aiir']"
                type="button"
                :disabled="any_loading"
                class="btn btn-ss"
                @click="props.do({cb: publishItemsToAiir, reload_data: false})">
          <loader v-if="loading.publish_aiir" small class="mr-2" />
          Publish to Aiir
        </button>
        <button v-if="customer.aiir_client_id !== null"
                v-perms="['aiir']"
                type="button"
                :disabled="any_loading"
                class="btn btn-ss"
                @click="props.do({cb: unpublishItemsFromAiir, reload_data: false})">
          <loader v-if="loading.unpublish_aiir" small class="mr-2" />
          Unpublish from Aiir
        </button>
        <button v-if="preferences.pluxbox"
                v-perms="['pluxbox']"
                type="button"
                :disabled="any_loading"
                class="btn btn-ss"
                @click="props.do({cb: publishItemsToPluxbox, reload_data: false})">
          <loader v-if="loading.publish_pluxbox" small class="mr-2" />
          Publish to Pluxbox
        </button>
        <button v-perms="['recorder']"
                type="button"
                :disabled="any_loading"
                class="btn btn-ss"
                @click="props.do({cb: setItemsAsRecording, reload_data: false})">
          <loader v-if="loading.recording" small class="mr-2" />
          Set as Recording
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: setItemsAsUpload, reload_data: false})">
          <loader v-if="loading.upload" small class="mr-2" />
          Set as Upload
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: massAssignArtworkToItem, reload_data: true})">
          <loader v-if="loading.artwork" small class="mr-2" />
          Update Artwork
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: assignItemsToFeed, reload_data: false})">
          <loader v-if="loading.assign_to_feed" small class="mr-2" />
          Assign to Feed
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: assignTagsToItems, reload_data: false})">
          <loader v-if="loading.tags" small class="mr-2" />
          Assign Tags
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: massUpdatePublishing })">
          <loader v-if="loading.publishing" small class="mr-2" />
          Update Publishing
        </button>
        <b-dropdown v-if="mixcloud_authorised" v-perms="['mixcloud']" variant="ss">
          <template #button-content>
            Mixcloud
          </template>
          <b-dropdown-item-button v-perms="['api.mixcloud.upload']" @click="props.do({cb: uploadItemsToMixCloud, reload_data: false})">
            <i class="fa fa-cloud-upload-alt" />Upload
          </b-dropdown-item-button>
          <b-dropdown-item-button v-perms="['web.mixcloud.profile']" @click="props.do({cb: mixcloudProfile, reload_data: false})">
            <i class="fa fa-user" />Mixcloud Profile
          </b-dropdown-item-button>
          <b-dropdown-divider v-perms="['api.mixcloud.unlink']" />
          <b-dropdown-item-button v-perms="['api.mixcloud.unlink']" variant="danger" @click="props.do({cb: unlinkItemsFromMixCloud, reload_data: false})">
            <i class="flaticon2-warning" />Unlink
          </b-dropdown-item-button>
        </b-dropdown>
        <button class="btn btn-ss-danger btn-icon-sm ml-1"
                type="button"
                :disabled="any_loading"
                @click="props.do({cb: deleteAllItems})">
          <loader v-if="loading.delete" small class="mr-2" />
          <i v-else class="flaticon2-warning" />
          Delete
        </button>
      </template>
      <!-- FILTERS -->
      <template #_filters>
        <f-data-table-filters :target="tableId"
                              :expanded="true">
          <template #name="props">
            <f-data-table-filter field="name"
                                 display="Name:"
                                 placeholder="Search name..."
                                 :scope="props" />
          </template>
          <template #desc="props">
            <f-data-table-filter field="description"
                                 display="Description:"
                                 placeholder="Search description..."
                                 :scope="props" />
          </template>
          <template #published="props">
            <f-data-table-filter>
              <label for="published-status-select">Published:</label>
              <multiselect v-model="published_multi_select_val"
                           name="published-status-select"
                           :options="[ { display: `Yes`, val: 1 }, { display: `No`, val: 0 } ]"
                           label="display"
                           track-by="val"
                           placeholder="Select a status..."
                           @input="(selected) => {
                             props.filterChange({
                               client: {
                                 op: (val) => {
                                   if (!selected) {
                                     return true;
                                   } else {
                                     return selected.val === val;
                                   }
                                 },
                                 field: `published`
                               },
                               server: {
                                 type: `scalar`,
                                 op: `eq`,
                                 field: `published`,
                                 value: selected.val,
                               }
                             })
                           }" />
            </f-data-table-filter>
          </template>
          <template #tags="props">
            <f-data-table-filter>
              <label for="tags-search">Search tags:</label>
              <multiselect v-model="ms_tag_values"
                           name="tags-search"
                           :options="auto_complete_tags"
                           track-by="uuid"
                           label="name"
                           :multiple="true"
                           placeholder="Search tags..."
                           @input="(event) => {
                             props.filterChange({
                               server: {
                                 type: `param`,
                                 field: `tags`,
                                 value: ms_tag_values.map(tag => tag.name).join(',')
                               }
                             });
                           }" />
            </f-data-table-filter>
          </template>
          <template #feeds="props">
            <f-data-table-filter>
              <label for="feeds-filter-search">Search {{ getFeedLabel() }}s:</label>
              <multiselect v-model="feed_filter_multi_select_val"
                           name="feeds-filter-select"
                           :options="feeds"
                           label="name"
                           track-by="uuid"
                           placeholder="Select a feed..."
                           @input="(selected) => {
                             const feed_obj = feeds.find(feed => feed.name === selected);
                             props.filterChange({
                               client: {
                                 value: selected,
                                 op: (val) => {
                                   if (!feed_obj) {
                                     return true;
                                   } else if (feed_obj.items.some(item => item.uuid === val)) {
                                     return true;
                                   }
                                   return false;
                                 },
                                 field: `uuid`
                               },
                               server: {
                                 type: `param`,
                                 op: `eq`,
                                 field: `feed_uuid`,
                                 value: selected.uuid
                               }
                             })
                           }" />
            </f-data-table-filter>
          </template>
          <template #orphaned="props">
            <f-data-table-filter>
              <label for="orphaned-filter-select"><span class="hover-tooltip" :title="`Orphaned ${getItemLabel()}s are ${getItemLabel()}s whos Storage file is missing (usually deleted).`">Orphaned</span> status:</label>
              <multiselect v-model="orphaned_filter_multi_select_val"
                           name="orphaned-filter-select"
                           :options="[`Only`, `Exclude`]"
                           placeholder="Select an 'Orphaned' status..."
                           @input="(selected) => {
                             selected = selected ? selected.toLowerCase() : null;
                             props.filterChange({
                               client: {
                                 op: (val) => {
                                   if (!selected) {
                                     return true;
                                   } else {
                                     if (selected === `only`) {
                                       return val.storage === null;
                                     } else {
                                       return val.storage !== null;
                                     }
                                   }
                                 },
                               },
                               server: {
                                 type: `param`,
                                 field: `orphaned`,
                                 value: selected,
                               },
                             });
                           }" />
            </f-data-table-filter>
          </template>
          <template #archived="props">
            <f-data-table-filter>
              <label for="archived-filter-select">Archived {{ getItemLabel() }}:</label>
              <multiselect v-model="archived_filter_multi_select_val"
                           name="archived-filter-select"
                           :options="archived_filter_ms_options"
                           label="display"
                           track-by="val"
                           :allow-empty="false"
                           deselect-label="Value is required."
                           placeholder="Select an archived status..."
                           @input="(selected) => {
                             if (selected !== `exclude`) {
                               props.filterChange({
                                 server: {
                                   type: `param`,
                                   field: `archived`,
                                   value: selected.val,
                                 },
                               });
                             }
                           }" />
            </f-data-table-filter>
          </template>
        </f-data-table-filters>
      </template>
      <!-- COLUMNS -->
      <template #thumb="props">
        <img :src="resolveItemArtworkUrl(props.row)"
             alt="Thumb"
             class="thumbnail rounded"
             style="width: 64px;">
      </template>
      <template #name="props">
        <active-passive :data-val="props.value" :input-id="`name-edit-${props.row.uuid}`">
          <template #ap-passive>
            <span>{{ props.value }}</span>
          </template>
          <template #ap-active>
            <input :id="`name-edit-${props.row.uuid}`"
                   type="text"
                   :value="props.value"
                   @change="(ev) => {
                     // update db:
                     props.do({
                       cb: () => {
                         quickEditItem(props.row, `name`, ev.target.value);
                         return false; // return false to stop FE reload
                       },
                     });
                   }">
          </template>
        </active-passive>
      </template>
      <template #description="props">
        <active-passive :data-val="props.value" :input-id="`desc-edit-${props.row.uuid}`">
          <template #ap-passive>
            <!-- just how it be sometimes -->
            <div style="min-height: 40px;">
              <read-more :text="props.value"
                         more-str="[ Expand ]"
                         less-str="[ Hide ]"
                         :max-chars="80" />
            </div>
          </template>
          <template #ap-active>
            <tip-tap id="`desc-edit-${props.row.uuid}`"
                     :value="props.value"
                     @input="(ev) => {
                       props.do({
                         cb: () => {
                           if (props.value !== ev) {
                             quickEditItem(props.row, 'description', ev);
                           }
                           return false;
                         }
                       });
                     }" />
          </template>
        </active-passive>
      </template>
      <template #published="props">
        <active-passive :data-val="props.value" :input-id="`published-edit-${props.row.uuid}`" :event-id="inline_published_ms_ev_id">
          <template #ap-passive>
            <span v-if="props.value === 1" style="color: green; font-weight: bold;">Yes</span>
            <span v-else>No</span>
          </template>
          <template #ap-active>
            <multiselect v-model="ms_inline_edit_store"
                         :options="published_ac_list"
                         :taggable="true"
                         track-by="val"
                         label="display"
                         class="active-ms-cell"
                         :allow-empty="false"
                         deselect-label="Must have one selected"
                         @select="(ev) => {
                           props.do({
                             cb: () => {
                               quickEditItem(
                                 props.row,
                                 `published`,
                                 ev.val,
                               );
                               return false;
                             }
                           });
                         }" />
          </template>
        </active-passive>
      </template>
      <template #publish_start="props">
        <span v-if="props.value">{{ formatDateTime(props.value) }}</span>
        <span v-else class="no-content-notice">None</span>
      </template>
      <template #publish_stop="props">
        <span v-if="props.value">{{ formatDateTime(props.value) }}</span>
        <span v-else class="no-content-notice">None</span>
      </template>
      <template #created_at="props">
        <span>{{ formatDateTime(props.value) }}</span>
      </template>
      <template #tags="props">
        <active-passive :data-val="props.value"
                        :input-id="`tags-edit-${props.row.uuid}`"
                        :event-id="`${inline_tags_ms_ev_id}-${props.row.uuid}`"
                        @ap-toggle="() => {
                          emit(`ap-update`, { id: `${inline_tags_ms_ev_id}-${props.row.uuid}`, val: props.value });
                          ms_inline_edit_store = props.value;
                        }">
          <template #ap-passive>
            <multi-select-tag-bag v-if="props.value.length" :tags-list="props.value" :limit="5" />
            <span v-else class="no-content-notice">None</span>
          </template>
          <template #ap-active>
            <multiselect v-model="ms_inline_edit_store"
                         :options="tags_ac_list"
                         :multiple="true"
                         :taggable="true"
                         track-by="uuid"
                         label="name"
                         class="active-ms-cell"
                         placeholder="Search and add new tags..."
                         @input="(ev) => {
                           props.do({
                             cb: () => {
                               const names_only = ms_inline_edit_store.map(tag => tag.name);
                               quickEditItem(props.row, `tags`, names_only);
                               return false;
                             }
                           });
                         }"
                         @tag="(tag) => {
                           props.do({
                             cb: () => {
                               addNewTag(tag);
                               const names = ms_inline_edit_store.map(tag => tag.name);
                               quickEditItem(props.row, `tags`, names);
                               return false;
                             }
                           })
                         }" />
          </template>
        </active-passive>
      </template>
      <template #actions="props">
        <b-dropdown split
                    block
                    variant="ss"
                    text="Edit"
                    :split-href="`/items/${props.row.uuid}/edit`">
          <b-dropdown-item-button @click="() => props.do({ cb: shareItem })">
            <i class="fa fa-share-alt" />Share
          </b-dropdown-item-button>
          <b-dropdown-item-button data-target="#item_preview"
                                  @click="() => previewItem(props.row)">
            <i class="fa fa-play" />Preview
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="mixcloud_authorised"
                                  v-perms="['api.mixcloud.upload']"
                                  @click="props.do({cb: uploadItemsToMixCloud, reload_data: false})">
            <i class="fa fa-cloud-upload-alt" />Upload to Mixcloud
          </b-dropdown-item-button>
          <b-dropdown-divider />
          <b-dropdown-item-button v-if="mixcloud_authorised"
                                  v-perms="['api.mixcloud.unlink']"
                                  variant="danger"
                                  @click="props.do({cb: unlinkItemsFromMixCloud, reload_data: false})">
            <i class="flaticon2-warning" />Unlink from Mixcloud
          </b-dropdown-item-button>
          <b-dropdown-item-button variant="danger" @click="() => { inline_selected_item = props.row; props.do({ cb: deleteAllItems }); }">
            <i class="flaticon2-warning" />Delete
          </b-dropdown-item-button>
        </b-dropdown>
      </template>
    </f-data-table>
    <mass-update-publishing-modal name="mass-update-publish" :callback="modalCallback" />
    <artwork-selection-modal name="artwork-select-modal" :callback="modalCallback" />
    <assign-to-feed-modal id="assign-to-feed-modal" name="assign-to-feed" :callback="modalCallback" />
    <assign-tags-to-items-modal id="assign-tags-to-items-modal" name="assign-tags-to-items" />
    <preview-modal name="preview-item" :is-feed="false" :entity="preview_item" />
    <delete-confirm-modal name="delete-confirm"
                          :entities-to-delete="target_items"
                          entity-type="item"
                          :callback="modalCallback">
      <div class="d-flex flex-column justify-content-center">
        <h4 style="text-align: center">
          Are you sure you want to delete {{ target_items.length }} Items?
        </h4>
        <div class="mt-4 pl-4">
          <ss-checkbox id="preserve-storage" v-model="preserve_assets_on_item_delete">
            <span class="ml-2">
              Preserve Storage Assets?
              <span v-tooltip="'Storage assets are deleted by default; tick this to preserve the associated storage asset after this item is deleted.'">
                <i class="ml-2 cursor-pointer fa fa-info-circle" />
              </span>
            </span>
          </ss-checkbox>
        </div>
      </div>
    </delete-confirm-modal>
    <social-share-modal id="share-item-confirm" name="social-share" :entity="item_to_share" />
  </section>
</template>

<script>
import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable";
import FDataTableFilters from "VuePartials/App/Storage/FDataTable/FDataTableFilters";
import FDataTableFilter from "VuePartials/App/Storage/FDataTable/FDataTableFilter";
import MultiSelectTagBag from "VueComponents/MultiSelectTagBag";
import ReadMore from 'VueComponents/ReadMoreComponent';
import DeleteConfirmModal from 'VueModals/DeleteConfirmModal';
import SocialShareModal from 'VueModals/SocialShareModal';
import AssignToFeedModal from 'VueModals/AssignToFeedModal';
import PreviewModal from 'VueModals/PreviewModal';
import ActivePassive from 'VueComponents/ActivePassive';
import ItemCreationForm from "VueComponents/ItemCreation/Form";

import { nullish } from 'Lib/type-sanity';
import { EditDispatcher } from 'Lib/ajax-util';

import {
  BDropdown,
  BDropdownItemButton,
  BDropdownDivider,
} from 'bootstrap-vue';

import {
  formatDate,
} from 'Helpers/formatters';
import AssignTagsToItemsModal from "VueModals/AssignTagsToItemsModal";
import MassUpdatePublishingModal from "VueModals/MassUpdatePublishingModal";
import ArtworkSelectionModal from "VueModals/ArtworkSelectionModal";
import VjsModal from 'VueModals/VjsModal';
import { chunked } from 'Lib/array-util';
import SsCheckbox from 'VueComponents/SsCheckbox.vue';
import TaggableModelMixin from 'VueMixins/TaggableModelMixin';

/** Max number of Items ids to send in query params, i.e for `mass-delete`. */
const API_MASS_ACTION_LIMIT = 16;

export default {
  components: {
    ArtworkSelectionModal,
    MassUpdatePublishingModal,
    AssignTagsToItemsModal,
    FDataTable,
    FDataTableFilters,
    FDataTableFilter,
    ReadMore,
    MultiSelectTagBag,
    BDropdown,
    BDropdownItemButton,
    BDropdownDivider,
    SocialShareModal,
    AssignToFeedModal,
    PreviewModal,
    ActivePassive,
    DeleteConfirmModal,
    ItemCreationForm,
    VjsModal,
    SsCheckbox,
  },
  mixins: [TaggableModelMixin],
  props: {
    excludedFeed: {
      type: String,
      required: false,
      default: null,
    },
    tableId: {
      type: String,
      required: false,
      default: `items-table`,
    },
  },
  data: function () {
    return {
      preserve_assets_on_item_delete: false,
      listen_timeout: 5000,
      loading: {
        archive: false,
        unarchive: false,
        recording: false,
        publish_aiir: false,
        unpublish_aiir: false,
        publish_pluxbox: false,
        upload: false,
        artwork: false,
        tags: false,
        publishing: false,
        delete: false,
        assign_to_feed: false,
        assign_tags: false,
      },
      inline_selected_item: undefined,
      item_create_modal: `item-create-modal`,
      qe_dispatcher: null,
      customer: null,
      preferences: null,

      mixcloud_upload_quick_alert: {
        title: 'Mixcloud Upload',
        message: 'Bulk uploads are limited to 4 items per request and can take time to reflect in your Mixcloud feed. If you have previously linked or changed the account please unlink the items first.',
        showCancel: true,
      },
      mixcloud_unlink_quick_alert: {
        title: 'Mixcloud Unlink',
        message: 'You are about to break the link between Timbre and Mixcloud for the selected item(s). This will not delete the item(s) from Mixcloud, but tags will be removed to avoid accidental re-upload.',
        showCancel: true,
      },
      set_as_recording_quick_alert: {
        title: 'Set as recording',
        message: 'Selected items will be set as recordings. Broadcast Start and Broadcast Stop fields will be available and are required for Aiir publishing.',
        showCancel: true,
      },
      item_to_share: {},
      data_uri: `/api/v1/items`,
      columns: [
        {
          field: `thumb`,
          display: `Thumbnail`,
          sortable: false,
          width: 70,
          clip_resistance: -1,
          toggleable: true,
        },
        {
          field: `name`,
          display: `Name`,
          sort_dir: `asc`,
          clip_resistance: 1,
        },
        {
          field: `description`,
          display: `Description`,
          width: 300,
          min_width: 200,
          toggleable: true,
        },
        {
          field: `published`,
          display: `Published`,
          toggleable: true,
        },
        {
          field: `publish_start`,
          display: `Publish Start`,
          toggleable: true,
        },
        {
          field: `publish_stop`,
          display: `Publish Stop`,
          toggleable: true,
        },
        {
          field: `created_at`,
          display: `Created at`,
          active_sort: true,
        },
        {
          field: `tags`,
          display: `Tags`,
          sortable: false,
          toggleable: true,
        },
        {
          field: `actions`,
          display: `Actions`,
          sortable: false,
          never_clip: true,
        },
      ],
      ms_inline_edit_store: null,
      published_multi_select_val: null,
      archived_filter_multi_select_val: {
        display: `Exclude (default)`,
        val: 0,
      },
      archived_filter_ms_options: [
        { display: `Exclude (default)`, val: `exclude` },
        { display: `Only`, val: `only` },
        { display: `Include`, val: `include` },
      ],
      tags_list: [],
      tags_debounce: undefined,
      feed_filter_multi_select_val: null,
      orphaned_filter_multi_select_val: null,
      feeds: [],
      selected_feed: ``,
      selected_items: [],
      preview_item: {},
      dummy_tag_model: ``,
      static_params: [
        {
          field: `with_player_data`,
          value: `true`,
        },
        (() => !nullish(this.excludedFeed) ? {
          field: `excluded_feed_uuid`,
          value: this.excludedFeed,
        } : {})(),
      ],
      return_data: {},
      mixcloud_authorised: window.mixcloud_authorised,
      modalCallback: null,
    };
  },
  computed: {
    target_items() {
      return this.inline_selected_item ? [
        this.inline_selected_item,
      ] : this.selected_items;
    },
    any_loading() {
      return Object.values(this.loading).some(flag => flag === true);
    },
    inline_tags_ms_ev_id() {
      return `inline-ap-tags-ms`;
    },
    inline_published_ms_ev_id() {
      return `inline-ap-published-ms`;
    },
    published_ac_list() {
      return [{ display: `Yes`, val: 1 }, { display: `No`, val: 0 }];
    },
  },
  asyncComputed: {
    artworks: {
      async get() {
        try {
          const res_data = (await axios.get(`/api/v1/artwork`))?.data;
          return this.fallback(res_data?.data, []);
        } catch (err) {
          window.handleAxiosError(err);
          return [];
        }
      },
      default: [],
    },
    feeds: {
      async get() {
        try {
          const res_data = (await axios.get(`/api/v1/feeds`))?.data;
          return this.fallback(res_data?.data, []);
        } catch (err) {
          window.handleAxiosError(err);
          return [];
        }
      },
      default: [],
    },
    tags_ac_list: {
      async get() {
        try {
          const res_data = (await axios.get('/api/v1/tags?type=item&per_page=10000'))?.data;
          return this.fallback(res_data?.data, []);
        } catch (err) {
          window.handleAxiosError(err);
          return [];
        }
      },
      default: [],
    },
  },
  beforeMount() {
    this.customer = window.user.customer;
    this.preferences = window.preferences;
  },
  mounted() {
    this.qe_dispatcher = new EditDispatcher({
      debounce_timeout: 2500,
      default_endpoint: this.data_uri,
    });
    this.initSocial();
    this.fetchTags();

    EventBus.$on([`item_preview-closed`], () => {
      $(`#item_preview`).modal(`hide`);
      this.preview_item = null;
    });

    EventBus.$on(`ap-toggle`, (ev) => {
      if (ev.id === this.inline_tags_ms_ev_id) {
        this.ms_inline_edit_store = ev.val;
      } else if (ev.id === this.inline_published_ms_ev_id) {
        this.ms_inline_edit_store = [
          {
            val: ev.val,
            display: ev.display ?? this.published_ac_list.find(opt => opt.val === ev.val).display,
          },
        ];
      }
    });

    EventBus.$on(`fdt-pagination`, (ev) => {
      this.return_data = {
        ...this.return_data,
        page: ev.pagination.current_page,
      };
    });
    EventBus.$on(`fdt-filter-change`, (ev) => {
      this.return_data = {
        ...this.return_data,
        filters: {
          ...this.return_data.filters,
          [ev.server.field]: ev.server,
        },
      };
    });

    EventBus.$on(`fdt-rows-selected`, (ev) => {
      this.selected_items = ev;
    });
  },
  methods: {
    async showItemCreationModal() {
      await this.fireModal(this.item_create_modal);
    },
    emit(event_name, payload) {
      EventBus.$emit(event_name, payload);
    },
    resolveItemArtworkUrl(item) {
      return this.fallback(
        item.artwork?.path,
        this.artworks.find(artwork => artwork.uuid === item.artwork_uuid)?.path,
        item.default_image_url ?? this.$missing_image_path,
      );
    },
    previewItem(row) {
      this.preview_item = row;
      this.fireModal(`preview-item`);
    },
    formatDate,
    async shareItem(item) {
      this.item_to_share = item;
      this.fireModal(`social-share`);
      return false;
    },
    getItemIds(items) {
      return items.map(item => item.uuid);
    },
    // TODO: this type of fn should go in a mixin
    handleResponse(res, success_message, error_message = `Error`) {
      if (res.data && res.data.success && success_message) {
        toastr.success(success_message);
      } else if (error_message) {
        if (res.data.error.message) {
          error_message = `${error_message}: ${res.data.error.message}`;
        }
        toastr.error(_.trim(error_message) || `Something went wrong!`);
      }
    },
    async doAjax(ids, method, options, absolute_path = false) {
      const config = {
        absolute_path: absolute_path,
        id_key: 'ids',
        endpoint: '',
        success_message: 'Success',
        error_message: 'Error',
        additional_data: {},
        ...options,
      };

      try {
        const post_data = {
          ...config.additional_data,
          ...{ [config.id_key]: ids },
        };

        const res = await method(`${config.absolute_path ? '' : this.data_uri}${config.endpoint}`, post_data);
        this.handleResponse(res, config.success_message, config.error_message);
      } catch (err) {
        window.handleAxiosError(err);
        return false;
      }
    },
    quickEditItem(item, field, value, request_config = {}) {
      this.qe_dispatcher.quickEdit(item, field, value, request_config);
      EventBus.$emit(`${this.tableId}-update-row`, {
        field,
        value,
        find: (row => row.uuid === item.uuid),
      });
    },
    async archiveItems(...items) {
      this.loading.archive = true;
      await this.doAjax(items.map(item => item.uuid), axios.post, {
        endpoint: `/archive`,
        success_message: `Successfully archived ${items.length} item(s)!`,
        error_message: `Error archiving item(s)`,
      });
      this.loading.archive = false;
    },
    async unarchiveItems(...items) {
      this.loading.unarchive = true;
      await this.doAjax(items.map(item => item.uuid), axios.post, {
        endpoint: `/restore`,
        success_message: `Successfully restored ${items.length} item(s)!`,
        error_message: `Error restoring item(s)`,
      });
      this.loading.unarchive = false;
    },
    async publishItemsToAiir(...items) {
      this.loading.publish_aiir = true;
      await this.doAjax(items.map(item => item.uuid), axios.post, {
        endpoint: `/aiir/publish`,
      });
      this.loading.publish_aiir = false;
    },
    async unpublishItemsFromAiir(...items) {
      this.loading.unpublish_aiir = true;
      await this.doAjax(items.map(item => item.uuid), axios.post, {
        endpoint: `/aiir/unpublish`,
      });
      this.loading.unpublish_aiir = false;
    },
    async publishItemsToPluxbox(...items) {
      this.loading.publish_pluxbox = true;
      await this.doAjax(items.map(item => item.uuid), axios.post, {
        endpoint: `/pluxbox/publish`,
      });
      this.loading.publish_pluxbox = false;
    },
    async setItemsAsRecording(...items) {
      const response = await this.quickAlert(this.set_as_recording_quick_alert);
      this.loading.recording = true;

      if (response.data.confirm) {
        await this.doAjax(items.map(item => item.uuid), axios.post, {
          endpoint: `/recording/set`,
          success_message: `Successfully set ${items.length} item(s) as Recording!`,
          error_message: `Error setting item(s) as Recording`,
        });
      }
      this.loading.recording = false;
    },
    async setItemsAsUpload(...items) {
      this.loading.upload = true;
      await this.doAjax(items.map(item => item.uuid), axios.post, {
        endpoint: `/upload/set`,
        success_message: `Successfully set ${items.length} item(s) as Upload!`,
        error_message: `Error setting item(s) as upload`,
      });
      this.loading.upload = false;
    },
    mixcloudProfile() {
      window.open('/mixcloud', "_self");
    },
    async uploadItemsToMixCloud(...items) {
      if (items.length > 1) {
        try {
          const response = await this.quickAlert(this.mixcloud_upload_quick_alert);

          if (response.data.confirm) {
            return this.doAjax(items.map(item => item.uuid), axios.post, {
              endpoint: 'api/v1/mixcloud/upload',
              success_message: `Uploading ${items.length} item(s) to Mixcloud`,
              error_message: 'Failed to upload item(s) to Mixcloud',
            }, true);
          }
        } catch (err) {
          console.error(err);
        }
      } else {
        return this.doAjax(items.map(item => item.uuid), axios.post, {
          endpoint: 'api/v1/mixcloud/upload',
          success_message: 'Uploading item to Mixcloud',
          error_message: 'Failed to upload item to Mixcloud',
        }, true);
      }
    },

    async unlinkItemsFromMixCloud(...items) {
      try {
        const response = await this.quickAlert(this.mixcloud_unlink_quick_alert);

        if (response.data.confirm) {
          return this.doAjax(items.map(item => item.uuid), axios.post, {
            endpoint: 'api/v1/mixcloud/unlink',
            success_message: `Item(s) have been unlinked from Mixcloud!`,
            error_message: 'Failed to unlink item(s) to Mixcloud',
          }, true);
        }
      } catch (err) {
        console.error(err);
      }
    },

    async deleteAllItems(...items) {
      this.loading.delete = true;
      this.modalCallback = async () => {
        const chunked_items = chunked(items, API_MASS_ACTION_LIMIT);

        // map api calls into true/false depending on success
        const responses = await Promise.all(chunked_items.map(async (chunk) => {
          const uuids_str = chunk.map(item => item.uuid).join(',');
          const url = new URL(`${window.location.origin}/api/v1/items/${uuids_str}`);
          if (this.preserve_assets_on_item_delete) {
            url.searchParams.append('preserve-asset', true);
          }

          const href = url.href;

          try {
            const response = await axios.delete(href);

            return response.data?.success === true;
          } catch (err) {
            window.handleAxiosError(err);
            return false;
          }
        }));

        const all_success = responses.every(response => response.data?.success === true);
        if (all_success) {
          toastr.success(`Successfully deleted ${items.length} items.`);
          return true;
        }

        const succeeded = responses.filter(response => response.data?.success);
        const errored = responses.filter(response => response.data?.error?.message);

        errored.forEach(err_response => toastr.error(err_response.data?.error?.message));

        if (errored.length && succeeded.length) {
          toastr.warning('Deletions partially successful; some Items were not deleted.');
        }
      };

      const modal_name = 'delete-confirm';
      const modal_res = await this.fireModal(modal_name);
      this.loading.delete = false;
      if (modal_res.data.confirm) return true; // if all went well, reload table

      // Otherwise, there might be a case where we got no result despite the callback being triggered
      // (somehow the modal closed _during_ its callback).
      // In that case we would be interested in continuing to listen for another result, up to a timeout

      // here we await a promise which resolves instantly if we got a hit
      // or the timeout threshold is reached
      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },

    async massAssignArtworkToItem(...items) {
      this.loading.artwork = true;
      this.modalCallback = ({ selected_artwork }) => this.doAjax(items.map(item => item.uuid), axios.post, {
        id_key: `item_uuids`,
        additional_data: {
          artwork_uuid: `${selected_artwork.uuid}`,
        },
        absolute_path: true,
        endpoint: `/api/v1/items/massAssignArtwork`,
        success_message: `Successfully assigned artwork to item(s)`,
        error_message: `Error while attempting to assign artwork to item(s)`,
      });

      const modal_name = 'artwork-select-modal';
      const modal_res = await this.fireModal(modal_name);
      this.loading.artwork = false;
      if (modal_res.data.confirm) return true;

      // see deleteAllItems comments
      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },
    async massUpdatePublishing(...items) {
      this.loading.publishing = true;
      this.modalCallback = data => this.doAjax(null, axios.post, {
        absolute_path: true,
        endpoint: '/api/v1/items/bulk-update-publishing',
        additional_data: {
          ...data,
          items: items.map(item => item.uuid),
        },
        success_message: 'Publish Settings Updated',
      });

      const modal_name = 'mass-update-publish';
      const modal_res = await this.fireModal(modal_name);
      this.loading.publishing = false;
      if (modal_res.data.confirm) return true;

      // see deleteAllItems comments
      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },
    async assignTagsToItems(...items) {
      this.loading.assign_tags = true;
      const modal_response = await this.fireModal(`assign-tags-to-items`);
      this.loading.assign_tags = false;
      if (!modal_response.data.confirm) {
        return false;
      }

      const item_uuids = items.map(item => item.uuid);
      const tags = modal_response.data.tags;
      const replace = modal_response.data.replace;
      const post_data = {
        items: item_uuids,
        tags: tags,
        replace: replace,
      };

      axios.post(`/api/v1/tags/tags-to-items`, post_data).then(() => {
        toastr.success("Updated item tags.");
        EventBus.$emit(`${this.tableId}-reload`);
      }).catch((error) => {
        this.handleAxiosError(error);
      });
    },
    async assignItemsToFeed(...items) {
      this.modalCallback = ({ feed }) => this.doAjax(items.map(item => item.uuid), axios.post, {
        id_key: `item_uuids`,
        absolute_path: true,
        endpoint: `/api/v1/feeds/${feed.uuid}/attachItems`,
        success_message: `Successfully added ${items.length} item(s) to ${feed.name}!`,
        error_message: `Error when attempting to add item(s) to feed ${feed.name}.`,
      });
      this.loading.assign_to_feed = true;

      const modal_name = 'assign-to-feed';
      const modal_res = await this.fireModal(modal_name);
      this.loading.assign_to_feed = false;
      if (modal_res.data.confirm) return true;

      // see deleteAllItems comments
      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "AssetsSass/_mixins" as *;

.active-ms-cell {
  min-width: 200px;
}

i[class*='flaticon'] {
  &::before {
    margin-right: 0.4em;
  }
}

.no-content-notice {
  filter: opacity(60%);
}

.thumbnail {
  width: 64px;
  height: auto;
}

.item-vti {
  @include no-input-vti;
}

</style>
